import React from "react";
import { Card, Tag, Alert, Button, Space } from "antd";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import { ProfileAlert, AlertActionTarget } from "./alerts";

interface AlertsSummaryProps {
    alerts: ProfileAlert[];
    onAction?: (target: AlertActionTarget) => void;
}

// "Needs attention" — rules-driven summary that replaces StatusBanner. Title
// icon is green when clear, amber otherwise; the count tag reads "All clear" or
// "N items". Each alert may carry an action button (e.g. View / Review).
const AlertsSummary: React.FC<AlertsSummaryProps> = ({ alerts, onAction }) => {
    const clear = alerts.length === 0;
    // Red header when anything is an error, so it agrees with the red canvas.
    const hasError = alerts.some((a) => a.severity === "error");
    const iconColor = clear ? "var(--ft-success)" : hasError ? "var(--ft-error)" : "var(--ft-warning)";
    const tagColor = clear ? "green" : hasError ? "red" : "gold";

    return (
        <Card
            size="small"
            data-testid="profile-alerts-summary"
            title={
                <span>
                    <ExclamationCircleOutlined style={{ color: iconColor, marginRight: 8 }} />
                    Needs attention
                </span>
            }
            extra={
                <Tag bordered={false} color={tagColor} style={{ marginInlineEnd: 0, fontWeight: 700 }}>
                    {clear ? "All clear" : `${alerts.length} ${alerts.length === 1 ? "item" : "items"}`}
                </Tag>
            }
        >
            {clear ? (
                <Alert
                    type="success"
                    showIcon
                    message="No outstanding issues — membership active and up to date"
                    style={{ borderRadius: 8 }}
                />
            ) : (
                <Space direction="vertical" size={8} style={{ width: "100%" }}>
                    {alerts.map((al, i) => (
                        <Alert
                            key={i}
                            type={al.severity}
                            showIcon
                            message={al.message}
                            style={{ borderRadius: 8 }}
                            action={
                                al.actionLabel && al.actionTarget ? (
                                    <Button
                                        size="small"
                                        type="text"
                                        style={{ fontWeight: 600 }}
                                        onClick={() => onAction?.(al.actionTarget!)}
                                    >
                                        {al.actionLabel}
                                    </Button>
                                ) : null
                            }
                        />
                    ))}
                </Space>
            )}
        </Card>
    );
};

export default AlertsSummary;
